import Link from 'next/link';
import { useState, useEffect, Fragment } from 'react';
import { HamburgerButton } from './HamburgerButton.jsx';
import { useAuth, getSignInUrl, getSignOutUrl } from '../../auth/';

function useModal() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(
    function() {
      if (isOpen === false) {
        return;
      }

      function handleOutsideClick(event) {
        if (event.target.closest('.primaryNav__popup')) {
          return;
        }

        setIsOpen(false);
      }

      function handleEscapePress(event) {
        if (event.keyCode === 27) {
          setIsOpen(false);
        }
      }

      document.body.addEventListener('touchstart', handleOutsideClick);
      document.body.addEventListener('click', handleOutsideClick);
      document.body.addEventListener('keydown', handleEscapePress);

      return () => {
        document.body.removeEventListener('touchstart', handleOutsideClick);
        document.body.removeEventListener('click', handleOutsideClick);
        document.body.removeEventListener('keydown', handleEscapePress);
      };
    },
    [isOpen]
  );

  return [isOpen, setIsOpen];
}

const NavLink = ({ title, href, level = 'regular', ...props }) => (
  <a href={href} {...props}>
    {title}
    <svg width="8" height="12" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7 6.7l.7-.7L2 .3.6 1.8 4.8 6 .6 10.2 2 11.7z"
        fill="#B9BFDF"
        fillRule="nonzero"
      />
    </svg>
    <style jsx>{`
      a {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 3px 6px 0;
        margin: 6px 0;
        font-size: 18px;
        font-weight: 600;
        border-bottom: solid 1px rgba(0, 0, 0, 0.08);
        color: var(--bgBlueish__tintedText);
        text-decoration: none;
      }
    `}</style>
    <style jsx>{`
      a {
        color: #${level === 'destroy' ? 'fb2b18' : '1939fb'};
      }
    `}</style>
  </a>
);

const InternalNavLink = ({ href, ...props }) => (
  <Link href={href} passHref>
    <NavLink {...props} />
  </Link>
);

const ExternalNavLink = ({ href, ...props }) => (
  <NavLink href={href} {...props} />
);

export function PrimaryNav() {
  const { user, isSignedIn } = useAuth();
  const [isOpen, setIsOpen] = useModal();

  return (
    <li className="primaryNav">
      <HamburgerButton
        onClick={() => setIsOpen(true)}
        withUserBadge={isSignedIn}
      />
      <div
        className={`primaryNav__popup ${
          isOpen ? 'primaryNav__popup--open' : ''
        }`}
      >
        <button
          className="primaryNav__closeButton"
          onClick={() => setIsOpen(false)}
        >
          Lukk meny
        </button>
        <h3 className="primaryNav__headline">
          Sammenligning og refinansiering
        </h3>
        <ul className="primaryNav__list">
          <li>
            <InternalNavLink title="Boliglån" href="/boliglan" />
          </li>
          <li>
            <InternalNavLink title="Forbrukslån" href="/forbrukslan" />
          </li>
        </ul>
        <h3 className="primaryNav__headline">Tjenester</h3>
        <ul className="primaryNav__list">
          <li>
            <InternalNavLink
              title="Boliglånskalkulator"
              href="/boliglan/lanekalkulator"
            />
          </li>
        </ul>
        <h3 className="primaryNav__headline">Nyttige artikler</h3>
        <ul className="primaryNav__list">
          <li>
            <InternalNavLink
              title="Refinansiering av boliglån"
              href="/boliglan/refinansiering"
            />
          </li>
          <li>
            <InternalNavLink
              title="Det du må vite om bilforsikring"
              href="/forsikring/bilforsikring"
            />
          </li>
          <li>
            <InternalNavLink
              title="Finansiering av bilkjøp"
              href="/privatokonomi/finansiere-bilkjop"
            />
          </li>
        </ul>
        <h3 className="primaryNav__headline">Penger.no</h3>
        <ul className="primaryNav__list">
          <li>
            <InternalNavLink title="Om oss" href="/om-tjenesten" />
          </li>
          <li>
            <InternalNavLink title="Kontakt" href="/kontakt" />
          </li>
        </ul>
        {isSignedIn ? (
          <>
            <h3 className="primaryNav__headline">Innlogget som {user.id}</h3>
            <ul className="primaryNav__list">
              <li>
                <ExternalNavLink
                  title="Logg ut"
                  level="destroy"
                  href={getSignOutUrl()}
                />
              </li>
            </ul>
          </>
        ) : (
          false && (
            <>
              <h3 className="primaryNav__headline">Innlogging</h3>
              <ul className="primaryNav__list">
                <li>
                  <ExternalNavLink
                    title="Logg inn med BankID"
                    href={getSignInUrl()}
                  />
                </li>
              </ul>
            </>
          )
        )}
      </div>
      <style jsx>{`
        .primaryNav {
          position: relative;
        }

        .primaryNav__popup {
          min-height: 400px;
          position: absolute;
          top: -4px;
          right: -9px;
          z-index: 1000;
          width: calc(100vw - 18px);
          padding: 18px;
          box-sizing: border-box;
          background-color: var(--bgBlueish);
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.08),
            0 1px 40px 0 rgba(0, 0, 0, 0.1);
          border-radius: 6px;
          pointer-events: none;
          transform: scale(0.95);
          opacity: 0;
          transform-origin: top right;
          transition: transform 0.2s, opacity 0.2s;
        }

        .primaryNav__popup--open {
          pointer-events: auto;
          transform: none;
          opacity: 1;
        }

        .primaryNav__popup p {
          line-height: 1.5;
          font-size: 18px;
          margin: 20px 0;
        }

        .primaryNav__closeButton {
          position: absolute;
          top: -9px;
          right: -9px;
          padding: 0;
          width: 59px;
          height: 59px;
          border: none;
          outline: none;
          font-size: 0;
          background-color: transparent;
          opacity: 0.9;
          cursor: pointer;
        }

        .primaryNav__closeButton:focus {
          opacity: 1;
        }

        .primaryNav__closeButton::before,
        .primaryNav__closeButton::after {
          content: '';
          position: absolute;
          left: 13px;
          top: 33px;
          width: 24px;
          height: 3px;
          background-color: var(--bgBlueish__text);
        }

        .primaryNav__closeButton::before {
          transform: rotate(45deg);
        }

        .primaryNav__closeButton::after {
          transform: rotate(-45deg);
        }

        .primaryNav__headline {
          margin: 19px 0 0 0;
          font-size: 16px;
          font-weight: 500;
          color: var(--bgBlueish__text);
        }

        .primaryNav__list {
          list-style: none;
          padding: 0;
        }

        @media screen and (min-width: 750px) {
          .primaryNav__popup {
            top: 0;
            right: 0;
            width: 480px;
          }
        }
      `}</style>
    </li>
  );
}
